import React, { useState, useMemo, useCallback } from 'react';
import { useAsyncList } from '@react-stately/data';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { addToast } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import GlossariesTab from '../../components/admin/glossaries/GlossariesTab';
import GlossaryFormModal from '../../components/admin/glossaries/GlossaryFormModal';
import { GlossariesFilters, useDeleteGlossary, useBulkDeleteGlossaries } from '@/hooks/queries/useGlossaries';
import { useDebounce } from '@/hooks/useDebounce';
import { Glossary } from '@/services/apis/glossaries';
import glossariesApi from '@/services/apis/glossaries';

const GlossariesContainer: React.FC = () => {
  const { t } = useTranslation('admin');

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedGlossaryId, setSelectedGlossaryId] = useState<string | null>(null);
  const [selectedGlossaryIds, setSelectedGlossaryIds] = useState<string[]>([]);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const filters: GlossariesFilters = useMemo(
    () => ({
      limit: 10,
      categoryId: selectedCategoryId === 'all' ? undefined : selectedCategoryId,
      search: debouncedSearch || undefined,
      sortBy: 'name',
      sortOrder: 'asc',
      language: 'en',
    }),
    [selectedCategoryId, debouncedSearch],
  );

  const list = useAsyncList<Glossary>({
    async load({ cursor }: { cursor?: string }) {
      const response = await glossariesApi.getGlossaries({
        ...filters,
        cursor: cursor,
      });
      const items = response.data || [];
      const hasMorePages = response.pageInfo?.hasMore || false;
      setHasMore(hasMorePages);
      return {
        items,
        cursor: response.pageInfo?.cursor || undefined,
      };
    },
  });

  const handleLoadMore = useCallback(() => {
    if (hasMore && !list.isLoading) {
      list.loadMore();
    }
  }, [hasMore, list]);

  const loaderRef = useInfiniteScroll({
    hasMore: hasMore && !list.isLoading,
    onLoadMore: handleLoadMore,
  });

  const deleteGlossaryMutation = useDeleteGlossary();
  const bulkDeleteGlossariesMutation = useBulkDeleteGlossaries();

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedCategoryId(undefined);
    list.reload();
  }, [list]);

  const handleEdit = useCallback((glossary: Glossary) => {
    setSelectedGlossaryId(glossary.id);
    setFormModalOpen(true);
  }, []);

  const handleDelete = useCallback((glossary: Glossary) => {
    setSelectedGlossaryId(glossary.id);
    setDeleteModalOpen(true);
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedGlossaryId(null);
    setFormModalOpen(true);
  }, []);

  const handleBulkDelete = useCallback(() => {
    if (selectedGlossaryIds.length > 0) {
      setBulkDeleteModalOpen(true);
    }
  }, [selectedGlossaryIds]);

  const confirmDelete = async () => {
    if (!selectedGlossaryId) return;
    try {
      await deleteGlossaryMutation.mutateAsync(selectedGlossaryId);
      addToast({
        title: t('glossaries.actions.deleteSuccess'),
        color: 'success',
      });
      setDeleteModalOpen(false);
      setSelectedGlossaryId(null);
      list.reload();
    } catch (error) {
      addToast({
        title: t('glossaries.actions.deleteError'),
        color: 'danger',
      });
    }
  };

  const confirmBulkDelete = async () => {
    if (selectedGlossaryIds.length === 0) return;
    try {
      await bulkDeleteGlossariesMutation.mutateAsync(selectedGlossaryIds);
      addToast({
        title: t('glossaries.actions.bulkDeleteSuccess', { count: selectedGlossaryIds.length }),
        color: 'success',
      });
      setBulkDeleteModalOpen(false);
      setSelectedGlossaryIds([]);
      list.reload();
    } catch (error) {
      addToast({
        title: t('glossaries.actions.bulkDeleteError'),
        color: 'danger',
      });
    }
  };

  const hasActiveFilters = !!(selectedCategoryId || searchQuery);

  // Reload list when filters change
  React.useEffect(() => {
    list.reload();
  }, [selectedCategoryId, debouncedSearch]);

  return (
    <>
      <GlossariesTab
        glossaries={list.items}
        isLoading={list.isLoading}
        isFetchingNextPage={list.isLoading}
        error={null}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedCategoryId={selectedCategoryId}
        onCategoryChange={setSelectedCategoryId}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        onEdit={handleEdit}
        onDelete={handleDelete}
        deleteModalOpen={deleteModalOpen}
        selectedGlossary={selectedGlossaryId ? list.items.find((g) => g.id === selectedGlossaryId) || null : null}
        onDeleteConfirm={confirmDelete}
        onDeleteModalClose={() => {
          setDeleteModalOpen(false);
          setSelectedGlossaryId(null);
        }}
        isDeleting={deleteGlossaryMutation.isPending}
        loadMoreRef={loaderRef}
        hasNextPage={hasMore}
        onCreate={handleCreate}
        selectedGlossaryIds={selectedGlossaryIds}
        onSelectionChange={setSelectedGlossaryIds}
        onBulkDelete={handleBulkDelete}
        bulkDeleteModalOpen={bulkDeleteModalOpen}
        onBulkDeleteConfirm={confirmBulkDelete}
        onBulkDeleteModalClose={() => {
          setBulkDeleteModalOpen(false);
          setSelectedGlossaryIds([]);
        }}
        isBulkDeleting={bulkDeleteGlossariesMutation.isPending}
      />

      <GlossaryFormModal
        isOpen={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setSelectedGlossaryId(null);
          list.reload();
        }}
        glossaryId={selectedGlossaryId}
      />
    </>
  );
};

export default GlossariesContainer;
